/**
 * Value pillars and their chart colours.
 *
 * The categorical order is fixed — Commerce, People, Audience, Brand,
 * Operations — because the palette was validated in that order. Reordering it
 * breaks the adjacent-colour contrast guarantee.
 */

export const PILLAR_IDS = ['commerce', 'people', 'audience', 'brand', 'operations'] as const;
export type PillarId = (typeof PILLAR_IDS)[number];

/** `extra` is a sixth step for series that outrun the five pillars. */
export type SeriesColorId = PillarId | 'extra';

export interface Pillar {
  readonly id: PillarId;
  readonly label: string;
}

export const PILLARS: readonly Pillar[] = [
  { id: 'commerce', label: 'Commerce' },
  { id: 'people', label: 'People' },
  { id: 'audience', label: 'Audience' },
  { id: 'brand', label: 'Brand' },
  { id: 'operations', label: 'Operations' },
];

const VAR: Record<SeriesColorId, string> = {
  commerce: '--c-commerce',
  people: '--c-people',
  audience: '--c-audience',
  brand: '--c-brand',
  operations: '--c-operations',
  extra: '--c-extra',
};

/** CSS colour for a pillar, as a `var()` reference. */
export const pillarColor = (id: SeriesColorId): string => `var(${VAR[id]})`;

/** A 12% tint of the pillar colour over white, for icon plates. */
export const pillarTint = (id: SeriesColorId): string =>
  `color-mix(in srgb, ${pillarColor(id)} 12%, white)`;

/** Categorical series palette, in validated order. */
export const CATEGORICAL: readonly string[] = [
  'var(--c-commerce)',
  'var(--c-people)',
  'var(--c-audience)',
  'var(--c-brand)',
  'var(--c-operations)',
  'var(--c-extra)',
];

/** Sequential ramp, light to dark, one hue. */
export const SEQUENTIAL: readonly string[] = [
  'var(--seq-1)',
  'var(--seq-2)',
  'var(--seq-3)',
  'var(--seq-4)',
  'var(--seq-5)',
  'var(--seq-6)',
  'var(--seq-7)',
];

/** First step of each ramp, used as the fallback for an out-of-range index. */
const SEQ_FIRST = 'var(--seq-1)';
const CAT_FIRST = 'var(--c-commerce)';

/** Picks a sequential step for `t` in [0, 1]. Out-of-range and NaN clamp. */
export function seqColor(t: number): string {
  const last = SEQUENTIAL.length - 1;
  const i = Math.max(0, Math.min(last, Math.round((Number.isFinite(t) ? t : 0) * last)));
  return SEQUENTIAL[i] ?? SEQ_FIRST;
}

/** Series colour by index, wrapping through the categorical palette. */
export const seriesColor = (index: number): string =>
  CATEGORICAL[index % CATEGORICAL.length] ?? CAT_FIRST;

/**
 * Text colour that stays legible on a sequential step.
 * Steps 4 and darker need white; the light end needs ink.
 */
export const onSeqText = (score0to100: number): string =>
  score0to100 > 72 ? '#fff' : 'var(--pnet-text)';
